import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, User, Users, X } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../shared/ui/icons/SubjectKindIcon";

/** What the option is. Kept local so this component owes nothing to a module. */
export type PatientOptionKind = "patient" | "couple" | "group";

export interface PatientOption {
    id: string;
    name: string;
    /**
     * Absent means an individual patient, so a caller that only offers
     * patients needs no change.
     */
    kind?: PatientOptionKind;
    /**
     * Who a couple or group is made of. Shown under the name and searched too:
     * a therapist looking for a pair usually remembers one of the people, not
     * what the pair was called.
     */
    members?: string[];
    /**
     * A training centre's student: the course they are enrolled in, so a
     * mentoring booked for them starts on it.
     */
    courseId?: string | null;
    /**
     * A line under the name that tells two records of one person apart — a
     * student's course. Searched too.
     */
    detail?: string | null;
}

/** What goes under the name: who a couple or group is, or a student's course. */
const subtitleOf = (option: PatientOption): string | null =>
    option.members?.length ? option.members.join(", ") : option.detail || null;

type KindFilter = "all" | PatientOptionKind;

/** In the order the list is read: people, then pairs, then groups. */
const KIND_ORDER: PatientOptionKind[] = ["patient", "couple", "group"];

const FILTER_KEY: Record<KindFilter, string> = {
    all: "patientFinder.filterAll",
    patient: "patientFinder.filterPatients",
    couple: "patientFinder.filterCouples",
    group: "patientFinder.filterGroups",
};

const iconFor = (option: PatientOption) =>
    option.kind === "group"
        ? GroupOfPeopleIcon
        : option.kind === "couple"
          ? Users
          : User;

/** The badge that says booking this commits more than one person. */
const BADGE_KEY: Partial<Record<PatientOptionKind, string>> = {
    couple: "patientFinder.coupleBadge",
    group: "patientFinder.groupBadge",
};

/**
 * How many to show before asking the therapist to narrow the search.
 *
 * A short caseload stays clickable; a long one stops pushing the rest of the
 * booking form out of the modal. Searching is always available either way.
 */
const VISIBLE_LIMIT = 6;

interface PatientFinderProps {
    patients: PatientOption[];
    selectedId: string | null;
    onSelect: (patientId: string | null) => void;
    disabled?: boolean;
    /** Overrides the field label; the upcoming view reads as a filter. */
    label?: string;
}

/**
 * Search-and-pick patient selector.
 *
 * Shared rather than owned by a module: Agendar, Pagos and the constellation
 * launcher all need the same control, and a caseload long enough to need
 * searching is long enough to need it everywhere.
 *
 * Once a patient is chosen the list collapses to a single row, so the rest of
 * the surrounding form stays visible.
 */
export default function PatientFinder({
    patients,
    selectedId,
    onSelect,
    disabled = false,
    label,
}: PatientFinderProps) {
    const { t } = useTranslation();
    const fieldLabel = label ?? t("patientFinder.label");
    const [query, setQuery] = useState("");
    // The list stays shut until asked for. A caseload is a page of names the
    // therapist did not ask to see, and on a form it pushes everything else
    // below the fold before they have typed anything.
    const [open, setOpen] = useState(false);
    const [kind, setKind] = useState<KindFilter>("all");

    // Only the kinds actually on offer get a filter, and with a single kind
    // there is nothing to tell apart, so no filter row at all.
    const counts = useMemo(() => {
        const byKind = new Map<PatientOptionKind, number>();
        patients.forEach((patient) => {
            const own = patient.kind ?? "patient";
            byKind.set(own, (byKind.get(own) ?? 0) + 1);
        });
        return byKind;
    }, [patients]);
    const offeredKinds = KIND_ORDER.filter((option) => counts.has(option));
    const showFilters = offeredKinds.length > 1;

    const selected = useMemo(
        () => patients.find((patient) => patient.id === selectedId) ?? null,
        [patients, selectedId],
    );

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const ofKind =
            kind === "all"
                ? patients
                : patients.filter(
                      (patient) => (patient.kind ?? "patient") === kind,
                  );
        if (!needle) return ofKind;
        return ofKind.filter(
            (patient) =>
                patient.name.toLowerCase().includes(needle) ||
                (patient.detail ?? "").toLowerCase().includes(needle) ||
                (patient.members ?? []).some((member) =>
                    member.toLowerCase().includes(needle),
                ),
        );
    }, [patients, query, kind]);

    const filtered = matches.slice(0, VISIBLE_LIMIT);
    const hiddenCount = matches.length - filtered.length;

    if (selected) {
        const SelectedIcon = iconFor(selected);
        return (
            <div className="flex flex-col gap-1 text-sm">
                <span className="font-medium">{fieldLabel}</span>
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-gray-700">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        <SelectedIcon className="h-4 w-4" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium">
                            {selected.name}
                        </span>
                        {subtitleOf(selected) ? (
                            <span className="truncate text-xs text-content-tertiary">
                                {subtitleOf(selected)}
                            </span>
                        ) : null}
                    </span>
                    {selected.kind && BADGE_KEY[selected.kind] && (
                        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                            {t(BADGE_KEY[selected.kind] as string)}
                        </span>
                    )}
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            onSelect(null);
                            setQuery("");
                            setKind("all");
                            // Reopened deliberately: clearing the choice is
                            // how a therapist asks to pick someone else.
                            setOpen(true);
                        }}
                        aria-label={t("patientFinder.change")}
                        className="text-content-tertiary hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-300"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{fieldLabel}</span>

            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-tertiary" />
                <input
                    type="text"
                    value={query}
                    disabled={disabled}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        if (!disabled) setOpen(true);
                    }}
                    // Guarded rather than trusting the disabled attribute: a
                    // focus event still reaches the handler, and opening a
                    // list nobody can pick from is a list that lies.
                    onFocus={() => {
                        if (!disabled) setOpen(true);
                    }}
                    aria-label={t("patientFinder.search")}
                    placeholder={
                        showFilters
                            ? t("patientFinder.searchAny")
                            : t("patientFinder.search")
                    }
                    className="field-base field-default bg-surface-sunken py-2.5 pl-9 pr-3"
                />
            </div>

            {showFilters && (
                // Always on show, not only once the list is open: it is what
                // tells the therapist couples and groups can be booked here.
                <div
                    role="group"
                    aria-label={t("patientFinder.filterLabel")}
                    className="flex flex-wrap gap-1.5 pt-1"
                >
                    {(["all", ...offeredKinds] as KindFilter[]).map(
                        (option) => {
                            const active = kind === option;
                            const count =
                                option === "all"
                                    ? patients.length
                                    : (counts.get(option) ?? 0);
                            return (
                                <button
                                    key={option}
                                    type="button"
                                    disabled={disabled}
                                    aria-pressed={active}
                                    onClick={() => {
                                        setKind(option);
                                        setOpen(true);
                                    }}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                                        active
                                            ? "border-brand-600 bg-brand-600 text-white"
                                            : "border-gray-200 text-content-secondary hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:hover:border-brand-500/50 dark:hover:text-brand-300"
                                    }`}
                                >
                                    {t(FILTER_KEY[option])}
                                    <span
                                        className={`tabular-nums ${active ? "text-white/80" : "text-content-tertiary"}`}
                                    >
                                        {count}
                                    </span>
                                </button>
                            );
                        },
                    )}
                </div>
            )}

            {!open ? null : filtered.length === 0 ? (
                <p className="py-4 text-center text-sm text-content-tertiary">
                    {patients.length === 0
                        ? t("patientFinder.empty")
                        : t("patientFinder.noMatches")}
                </p>
            ) : (
                <ul className="max-h-48 space-y-1 overflow-y-auto">
                    {filtered.map((patient) => {
                        const Icon = iconFor(patient);
                        const badge = patient.kind
                            ? BADGE_KEY[patient.kind]
                            : undefined;
                        return (
                            <li
                                key={`${patient.kind ?? "patient"}:${patient.id}`}
                            >
                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => {
                                        onSelect(patient.id);
                                        setOpen(false);
                                    }}
                                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:opacity-50 dark:hover:bg-gray-800/50"
                                >
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                        <Icon className="h-4 w-4" />
                                    </span>
                                    <span className="flex min-w-0 flex-1 flex-col">
                                        <span className="truncate font-medium">
                                            {patient.name}
                                        </span>
                                        {subtitleOf(patient) ? (
                                            <span className="truncate text-xs text-content-tertiary">
                                                {subtitleOf(patient)}
                                            </span>
                                        ) : null}
                                    </span>
                                    {badge && (
                                        // Named, not just iconified: an icon alone
                                        // does not tell a screen reader that
                                        // booking this commits several people.
                                        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                                            {t(badge)}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {hiddenCount > 0 && (
                <p className="pt-1 text-center text-xs text-content-tertiary">
                    {t("patientFinder.andMoreKeepTyping", {
                        count: hiddenCount,
                    })}
                </p>
            )}
        </div>
    );
}
